import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Briefcase, Clock, Users, ChevronRight } from 'lucide-react';
import { Job } from '../../services/jobService';
import { useNavigate } from 'react-router-dom';

// Strip markdown formatting for preview display
const stripMarkdown = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/#{1,6}\s+/g, '') // Remove headers
    .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold
    .replace(/\*([^*]+)\*/g, '$1') // Remove italic
    .replace(/`([^`]+)`/g, '$1') // Remove inline code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links, keep text
    .replace(/^[-*+]\s+/gm, '') // Remove bullet points
    .replace(/^\d+\.\s+/gm, '') // Remove numbered lists
    .replace(/>\s+/g, '') // Remove blockquotes
    .replace(/\n+/g, ' ') // Replace newlines with spaces
    .trim();
};

interface JobCardProps {
  job: Job;
  index?: number;
}

const JobCard: React.FC<JobCardProps> = ({ job, index = 0 }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/careers/${job.id}`);
  };

  const formatDeadline = (dateString?: string) => {
    if (!dateString) return 'No deadline';

    const deadline = new Date(dateString);

    // Check if date is invalid
    if (isNaN(deadline.getTime())) return 'No deadline';

    const now = new Date();
    const diffTime = deadline.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // If deadline has passed
    if (diffDays < 0) return 'Closed';

    // If deadline is today
    if (diffDays === 0) return 'Ends today';
    // If deadline is tomorrow
    if (diffDays === 1) return 'Ends tomorrow';
    // If deadline within a week
    if (diffDays < 7) return `Ends in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
    // If deadline within a month
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `Ends in ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
    }
    // Otherwise show months
    const months = Math.floor(diffDays / 30);
    return `Ends in ${months} ${months === 1 ? 'month' : 'months'}`;
  };

  const getDepartmentColor = (department?: string) => {
    const colors: { [key: string]: string } = {
      'Engineering': 'from-blue-500/80 to-blue-600/85',
      'AI Research': 'from-purple-500/80 to-purple-600/85', 
      'Design': 'from-pink-500/80 to-pink-600/85',
      'Data Science': 'from-emerald-500/80 to-emerald-600/85',
      'Product': 'from-orange-500/80 to-orange-600/85',
      'Marketing': 'from-indigo-500/80 to-indigo-600/85',
    };
    return colors[department || ''] || 'from-primary-600/80 to-primary-700/85';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      onClick={handleClick}
      className="group cursor-pointer h-full"
    >
      <div className="relative bg-gradient-to-br from-gray-800/60 to-gray-900/40 backdrop-blur-xl border border-gray-700/60 rounded-xl p-6 shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-primary-500/10 hover:border-primary-400/30 transition-all duration-300 h-full flex flex-col">
        
        {/* Header with Title and Department Badge */}
        <div className="flex items-start justify-between mb-3">
          {/* Job Title */}
          <h3 className="text-xl font-semibold text-white group-hover:text-primary-400 transition-colors line-clamp-2 flex-1 pr-3" title={job.title}>
            {job.title}
          </h3>

          {/* Department Badge - Inline with title */}
          <div className="flex-shrink-0">
            <div className={`relative bg-gradient-to-r ${getDepartmentColor(job.department)} px-3 py-1.5 rounded-full border border-gray-600/30 backdrop-blur-sm group-hover:scale-105 transition-all duration-300`}>
              <span className="text-xs font-bold text-white uppercase whitespace-nowrap">{job.department}</span>
            </div>
          </div>
        </div>

        {/* Job Description */}
        <p className="text-gray-300 text-sm mb-4 line-clamp-3 flex-grow">
          {job.displayDescription || stripMarkdown(job.description || '')}
        </p>

        {/* Job Details */}
        <div className="flex flex-wrap gap-4 mb-4">
          {job.location && (
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate" title={job.location}>{job.location}</span>
            </div>
          )}
          {job.jobType && (
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Briefcase className="w-4 h-4 flex-shrink-0" />
              <span className="truncate" title={job.jobType}>{job.jobType}</span>
            </div>
          )}
          {job.level && (
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Users className="w-4 h-4 flex-shrink-0" />
              <span className="truncate" title={job.level}>{job.level}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-700/50 mt-auto">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>{formatDeadline(job.closingDate)}</span>
          </div>
          <div className="flex items-center gap-2 text-primary-400 group-hover:text-primary-300 transition-colors">
            <span className="text-sm font-medium">View Details</span>
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform flex-shrink-0" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default JobCard;